import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicAuditApi } from '../api';
import {
  Loader2,
  Check,
  CheckSquare,
  Calendar,
  User,
  Users,
  Building2,
  ClipboardCheck,
  FileText,
  ArrowLeft,
  Shield,
  BadgeCheck,
} from 'lucide-react';

const STATUS_CONFIG = {
  planned: { label: 'Planejada', color: 'badge-gray' },
  scheduled: { label: 'Agendada', color: 'badge-blue' },
  in_progress: { label: 'Em Andamento', color: 'badge-yellow' },
  completed: { label: 'Concluída', color: 'badge-green' },
  cancelled: { label: 'Cancelada', color: 'badge-red' },
};

const AUDIT_TYPES = [
  { value: 'internal', label: 'Interna' },
  { value: 'external', label: 'Externa' },
  { value: 'supplier', label: 'Fornecedor' },
  { value: 'certification', label: 'Certificação' },
  { value: 'surveillance', label: 'Manutenção' },
  { value: 'process', label: 'Processo' },
];

const RESULT_LABELS = {
  conforming: { label: 'Conforme', color: 'badge-green' },
  non_conforming: { label: 'Não Conforme', color: 'badge-red' },
  improvement: { label: 'Melhorar', color: 'badge-yellow' },
  not_applicable: { label: 'N/A', color: 'badge-gray' },
  not_verified: { label: 'Não Verificado', color: 'badge-yellow' },
};

const PublicAudit = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await publicAuditApi.get(token);
        setData(res.data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B1220' }} className="flex items-center justify-center">
        <Loader2 className="animate-spin text-green-500" size={32} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B1220' }} className="flex items-center justify-center p-6">
        <div className="card p-8 max-w-md w-full text-center">
          <Shield size={40} className="mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-bold text-white mb-2">Auditoria não encontrada</h1>
          <p className="text-gray-400 text-sm">
            O link pode estar incorreto ou a auditoria pode ter sido removida.
          </p>
        </div>
      </div>
    );
  }

  const { audit, items } = data;
  const statusConfig = STATUS_CONFIG[audit.status] || STATUS_CONFIG.planned;
  const typeLabel = AUDIT_TYPES.find((t) => t.value === audit.audit_type)?.label || audit.audit_type;

  return (
    <div style={{ minHeight: '100vh', background: '#0B1220' }}>
      <div className="mx-auto max-w-4xl px-4 py-8 space-y-6">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #2563EB, #00D4CB)' }}
          >
            <FileText size={20} style={{ color: '#fff' }} />
          </div>
          <div>
            <p className="text-white font-semibold">QualyX+ SGQ</p>
            <p className="text-gray-400 text-xs">Agenda de Auditoria Interna</p>
          </div>
        </div>

        {/* Audit header */}
        <div className="card p-6">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="font-mono text-green-400">{audit.code}</span>
            <span className={`badge ${statusConfig.color}`}>{statusConfig.label}</span>
            <span className="badge badge-gray">{typeLabel}</span>
            {audit.planned_date && (
              <span className="badge badge-gray">
                <Calendar size={12} className="mr-1" />
                {new Date(audit.planned_date).toLocaleDateString('pt-BR')}
              </span>
            )}
            {audit.certificates && audit.certificates.length > 0 && (
              <span className="badge badge-purple flex items-center gap-1">
                <BadgeCheck size={12} />
                {audit.certificates.map((c) => c.name).join(' • ')}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{audit.title}</h1>
          {audit.description && <p className="text-gray-300 text-sm whitespace-pre-wrap">{audit.description}</p>}
          {audit.areas && <p className="text-gray-400 text-sm mt-2">Áreas: {audit.areas}</p>}
        </div>

        {/* Items */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <ClipboardCheck size={20} /> Itens da Auditoria ({items.length})
          </h2>

          {items.length === 0 ? (
            <div className="card p-8 text-center text-gray-400">
              <p>Nenhum item cadastrado nesta auditoria.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {items.map((item, idx) => {
                const result = RESULT_LABELS[item.result];
                return (
                  <div key={item.id} className="card p-4">
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-700 text-white font-semibold text-sm shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        {item.requirement && (
                          <span className="block text-xs text-gray-400">{item.requirement}</span>
                        )}
                        <p className="text-white text-sm font-medium">{item.question}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {result && <span className={`badge ${result.color}`}>{result.label}</span>}
                          {item.certificates && item.certificates.length > 0 &&
                            item.certificates.map((c) => (
                              <span key={c.id} className="badge badge-purple flex items-center gap-1">
                                <BadgeCheck size={12} /> {c.name}
                              </span>
                            ))}
                          {item.area && (
                            <span className="badge badge-gray flex items-center gap-1">
                              <Building2 size={12} /> {item.area}
                            </span>
                          )}
                          {item.auditee && (
                            <span className="badge badge-gray flex items-center gap-1">
                              <User size={12} /> {item.auditee}
                            </span>
                          )}
                          {item.day && (
                            <span className="badge badge-gray flex items-center gap-1">
                              <Calendar size={12} /> {new Date(item.day).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                          {item.auditors && (
                            <span className="badge badge-gray flex items-center gap-1">
                              <Users size={12} /> {item.auditors}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 mt-3">
                          <span
                            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md ${
                              item.seen ? 'bg-green-500/15 text-green-400' : 'bg-gray-800 text-gray-500'
                            }`}
                          >
                            <Check size={14} /> Visto pelo Auditado
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md ${
                              item.auditor_confirmed
                                ? 'bg-green-500/15 text-green-400'
                                : 'bg-gray-800 text-gray-500'
                            }`}
                          >
                            <CheckSquare size={14} /> Confirmação do Auditor
                          </span>
                        </div>

                        {(item.sectors && item.sectors.length > 0) && (
                          <div className="mt-4 space-y-3">
                            <p className="text-xs font-medium text-gray-400 flex items-center gap-1">
                              <Building2 size={12} /> Setores ({item.sectors.length})
                            </p>
                            {item.sectors.map((sec) => (
                              <div key={sec.id} className="border border-gray-700 rounded-lg p-3 bg-gray-800/40">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                  <span className="text-sm font-semibold text-white">
                                    {sec.sector_name || sec.sector_code || 'Setor'}
                                  </span>
                                  {sec.sector_code && (
                                    <span className="badge badge-gray">{sec.sector_code}</span>
                                  )}
                                  <span
                                    className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md ml-auto ${
                                      sec.visto ? 'bg-green-500/15 text-green-400' : 'bg-gray-800 text-gray-500'
                                    }`}
                                  >
                                    <Check size={12} /> {sec.visto ? 'Com visto' : 'Sem visto'}
                                  </span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-400">
                                  {sec.auditor_name && (
                                    <span className="flex items-center gap-1">
                                      <User size={12} /> Auditor: <strong className="text-gray-200">{sec.auditor_name}</strong>
                                    </span>
                                  )}
                                  {sec.scheduled_at && (
                                    <span className="flex items-center gap-1">
                                      <Calendar size={12} /> {new Date(sec.scheduled_at).toLocaleString('pt-BR')}
                                    </span>
                                  )}
                                </div>
                                {sec.audited_subject && (
                                  <p className="mt-2 text-xs text-gray-300">
                                    <span className="text-gray-500">O que será auditado:</span> {sec.audited_subject}
                                  </p>
                                )}
                                {sec.observation && (
                                  <p className="mt-1 text-xs text-gray-300">
                                    <span className="text-gray-500">Observação:</span> {sec.observation}
                                  </p>
                                )}
                                {sec.visto_at && (
                                  <p className="mt-1 text-[11px] text-green-400/80">
                                    Visto registrado em {new Date(sec.visto_at).toLocaleString('pt-BR')}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-center pt-4">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-green-400 transition-colors">
            <ArrowLeft size={14} /> Acessar o sistema QualyX+
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicAudit;
